/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Shared bits for the Managed MLOps screens (Catalog / My instances tabs).

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { managedServiceApi } from '../../services/api';
import { PageHeader, Tabs, Tag, Tone } from '../console/ui';
import type {
  ManagedServiceInstance,
  ManagedServiceProduct,
  ManagedServiceStatus,
  ManagedServiceTier,
} from '../../types/managedServices';

export const PRODUCT_ORDER: ManagedServiceProduct[] = ['mlflow', 'kfp', 'dvc', 'metaflow'];

export const PRODUCT_NAMES: Record<ManagedServiceProduct, string> = {
  mlflow: 'MLflow',
  kfp: 'Kubeflow Pipelines',
  dvc: 'DVC',
  metaflow: 'Metaflow',
};

const POLLING_STATUSES: ManagedServiceStatus[] = ['provisioning', 'deleting'];

const STATUS_TONE: Record<ManagedServiceStatus, Tone> = {
  provisioning: 'info',
  active: 'ok',
  suspended: 'warn',
  failed: 'err',
  deleting: 'warn',
  deleted: 'err',
};

export const ManagedStatusTag: React.FC<{ status: ManagedServiceStatus; failureReason?: string }> = ({
  status,
  failureReason,
}) => (
  <span title={status === 'failed' && failureReason ? failureReason : undefined}>
    <Tag tone={STATUS_TONE[status] || 'off'} className={status === 'provisioning' || status === 'deleting' ? 'animate-pulse' : ''}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Tag>
  </span>
);

/** All of the user's managed instances; polls while any row is transitioning. */
export const useManagedInstances = () =>
  useQuery({
    queryKey: ['managed-services'],
    queryFn: () => managedServiceApi.list().then((res) => res.data as ManagedServiceInstance[]),
    refetchInterval: (query) => {
      const rows = query.state.data as ManagedServiceInstance[] | undefined;
      const isPolling = (rows || []).some((i) => POLLING_STATUSES.includes(i.status));
      return isPolling ? 5000 : false;
    },
  });

/** Router state used by "Re-order" to open the order dialog prefilled. */
export interface ReorderState {
  reorder: {
    product: ManagedServiceProduct;
    tier: ManagedServiceTier;
    storage_gb: number;
    name: string;
  };
}

/** Eyebrow + title + Catalog / My instances route tabs shared by both screens. */
export const ManagedHeader: React.FC<{ subtitle?: React.ReactNode; actions?: React.ReactNode }> = ({
  subtitle,
  actions,
}) => {
  const { data } = useManagedInstances();
  const active = (data || []).filter((i) => i.status === 'active').length;
  return (
    <>
      <div className="flex flex-col gap-1">
        <span className="ps-eyebrow">QuickPods</span>
        <PageHeader title="Managed MLOps" subtitle={subtitle} actions={actions} />
      </div>
      <Tabs
        label="MLOps sections"
        tabs={[
          { label: 'Catalog', to: '/managed-mlops', end: true, badge: PRODUCT_ORDER.length },
          { label: 'My instances', to: '/managed-mlops/instances', badge: data ? `${active} active` : undefined },
        ]}
      />
    </>
  );
};
